import { useEffect } from 'react';

export interface KeyboardShortcutHandlers {
  escape?: () => void;
  search?: () => void;
  navigate?: (direction: -1 | 1) => void;
  select?: () => void;
}

export function useKeyboardShortcuts(
  handlers: KeyboardShortcutHandlers,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      
      // Don't trigger shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || 
                      target.tagName === 'TEXTAREA' || 
                      target.isContentEditable;
      
      // Escape - always works
      if (e.key === 'Escape') {
        e.preventDefault();
        handlers.escape?.();
        return;
      }
      
      // Ctrl/Cmd + K - focus search
      if (modKey && e.key === 'k') {
        e.preventDefault();
        handlers.search?.();
        return;
      }
      
      // Arrow keys - only when not in input
      if (!isInput) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          handlers.navigate?.(-1);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          handlers.navigate?.(1);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          handlers.select?.();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlers, enabled]);
}
